import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FolderKanban, Loader2, Plus } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { deleteProject, getProjects, type Project } from "../../../lib/projects";
import { onPinsChanged } from "../../../lib/projectPins";
import { Btn, EmptyState, PageHead, SortHead, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { NewProjectModal } from "./NewProjectModal";
import { PortfolioStrip } from "./PortfolioStrip";
import { ProjectsExport } from "./ProjectsExport";
import { PROJECT_COLS, ProjectsListRow } from "./ProjectsListRow";
import { PinnedProjects } from "./PinnedProjects";
import { ProjectsToolbar } from "./ProjectsToolbar";
import { RoadmapBoard } from "./Roadmap";
import {
  PAGE_SIZE,
  clearFilters,
  listParams,
  nextSort,
  portfolioParams,
  readView,
  writeView,
  type ListView,
  type Sort,
} from "./listView";

/**
 * The project list.
 *
 * Two views over one query. The list is the register - everything, sortable by
 * eye, with the money on it. The roadmap is the same records grouped by when
 * they happen, which is a board because that is the shape of the question
 * ("what is running, what is next"), not because a board looks busier.
 *
 * What is being asked for lives in the URL rather than in component state. That
 * is not tidiness: the roadmap is where cards get dragged, and a reload - after
 * a deploy, after a crash, after opening a project in the same tab and coming
 * back - used to drop the operator back into an unfiltered list on page one. It
 * also makes a filtered register something you can send to somebody.
 *
 * THE RULES FOR THAT URL ARE NOT IN THIS FILE. `listView.ts` beside it owns the
 * shape of the question, the parsing, the writing, the sort cycle and the two
 * sets of request parameters, with a test on all of it. They moved out the round
 * the register grew from two filters to seven: what a hostile link does, which
 * fields may vanish from the address bar, and which projects the money above the
 * list is being asked about are rules, and none of them can be checked by
 * looking at the screen (AGENTS.md §4). What is left here is the wiring - fetch,
 * draw, and keep the page number honest.
 */
export function ProjectsList() {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  // Both memos hang off `sp`, whose identity react-router keeps stable for a
  // given query string. Without that, `readView` would build a new object on
  // every render and `load` would refire forever.
  const view = useMemo(() => readView(sp), [sp]);
  const params = useMemo(() => listParams(view), [view]);
  const { q, page } = view;
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState(q);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Project | null>(null);
  /**
   * Bumped whenever a pin moves anywhere on this screen.
   *
   * The strip above holds one server answer and cannot patch it from a row: a
   * project pinned from page three is not in the strip's list at all, so there
   * is nothing to insert - only a question to ask again. A number is the whole
   * message, because the strip does not care WHICH pin moved.
   */
  const [pinEpoch, setPinEpoch] = useState(0);

  /** One change to what the list is showing, written into the address bar. */
  const patchView = useCallback(
    (change: Partial<ListView>, replace = false) =>
      setSp((prev) => writeView({ ...readView(prev), ...change }), { replace }),
    [setSp],
  );

  /**
   * A new question is asked from the top.
   *
   * The page reset travels in the SAME write as the filter, so no render sees
   * the new tag beside the old page number, and nothing resets the page
   * afterwards - which is what would throw away a page that arrived in the URL
   * on purpose. It matters more with seven filters than it did with one: a
   * filter that left the pager on page 7 draws an empty card over a full
   * register, and every operator reads that as "no results".
   *
   * A change that IS the page (the pager) or the view (the tabs) passes through
   * untouched: resetting the page inside the call that sets it would be a loop,
   * and switching to the board and back must not lose the operator's place.
   */
  const onFilter = useCallback(
    (change: Partial<ListView>) =>
      patchView("page" in change || "view" in change ? change : { ...change, page: 1 }),
    [patchView],
  );

  /** Every filter off at once, the sort and the view left where they are. The
   *  list of what "every filter" means lives in `listView.ts`, so the day an
   *  eighth one is added this button follows it without an edit here. */
  const onClear = useCallback(() => setSp((prev) => writeView(clearFilters(readView(prev)))), [setSp]);

  /**
   * A column head, clicked.
   *
   * The cycle itself is `nextSort`, because which direction a column reads
   * FIRST is a property of the column and not of this component: a deadline
   * opens soonest-first, a budget largest-first, a name A to Z. The third click
   * puts the register back in its own order (newest first), which is the only
   * way home - there is no "created" column to click.
   */
  const onSort = useCallback(
    (column: Sort) =>
      setSp((prev) => {
        const current = readView(prev);
        return writeView({ ...current, ...nextSort(current, column) });
      }),
    [setSp],
  );

  // The term this screen last committed. A query that differs from it arrived
  // from somewhere else - the back button, a link somebody opened - and belongs
  // in the box, rather than being typed over by a buffer holding the old word.
  const committed = useRef(q);
  useEffect(() => {
    if (q === committed.current) return;
    committed.current = q;
    setQInput(q);
  }, [q]);

  // The typing buffer becomes the query once the operator stops. `replace`,
  // because one history entry per keystroke turns the back button into a
  // machine that spells the search term backwards one letter at a time.
  useEffect(() => {
    const next = qInput.trim();
    if (next === q) return;
    const id = setTimeout(() => {
      committed.current = next;
      patchView({ q: next, page: 1 }, true);
    }, 300);
    return () => clearTimeout(id);
  }, [qInput, q, patchView]);

  const goPage = useCallback((next: number) => patchView({ page: Math.max(1, next) }), [patchView]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `params` is the whole question, including the roadmap's exemptions and
      // the deadline days turned into instants. Nothing is assembled here, so
      // the export beside the page head and the strip above the list cannot end
      // up describing a different set of projects.
      const res = await getProjects(params);
      // A page number can now arrive from outside and point past the end of a
      // set that has shrunk since - a link sent last week, a bookmark kept for a
      // month. An empty card under a filter reads as "the filter found nothing",
      // so walk back to the start of what does exist instead of drawing it. The
      // guard cannot loop: it only fires above page 1 and it lands on page 1.
      if (res.rows.length === 0 && res.total > 0 && page > 1) {
        patchView({ page: 1 }, true);
        return;
      }
      setRows(res.rows);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [params, page, patchView]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchRow = (next: Project) => setRows((prev) => prev.map((x) => (x.id === next.id ? { ...x, ...next } : x)));

  // A pin written from the SIDEBAR, which is on screen beside this list and has
  // no idea the register is open (`shell/RailPins.tsx`). Without this the row
  // keeps wearing its "Pinned" badge after the rail has already dropped it, and
  // the strip above the list - which does refetch - contradicts the row.
  //
  // The flag is patched from the event rather than refetched: `load` puts the
  // whole table behind a spinner, and a bookmark is not worth twenty-five rows.
  // Rows this list does not hold fall through, which is what makes it safe to
  // run on every pin event, including the ones its own row menus just made.
  useEffect(
    () =>
      onPinsChanged(({ id, pinned }) =>
        setRows((prev) => prev.map((x) => (x.id === id ? { ...x, pinned } : x))),
      ),
    [],
  );

  async function removeProject(p: Project) {
    await deleteProject(p.id);
    setRows((prev) => prev.filter((x) => x.id !== p.id));
    setTotal((n) => Math.max(0, n - 1));
    setConfirmDel(null);
  }

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead
        title={t("projects.title")}
        sub={t("projects.count", { n: total })}
        actions={
          <>
            {/* Only over the list. The roadmap is a board of everything, and
                "this page only" is a sentence that means nothing there - the
                register is the thing an operator hands to somebody else. */}
            {view.view === "list" ? (
              <ProjectsExport params={params} pageRows={rows} total={total} />
            ) : null}
            <Btn tone="brand" onClick={() => setShowNew(true)}>
              <Plus className="size-4" />
              {t("projects.add")}
            </Btn>
          </>
        }
      />

      {/* The rollup over the same set the list is about to draw: both parameter
          objects come out of the same `narrowing()` in `listView.ts`, so the
          header cannot total a wider portfolio than the rows beneath it. That
          now includes the archive - the filter most able to leave a project's
          budget in the header while its row is gone from the list - and the
          roadmap's exemption for the status filter, since a header that kept it
          would describe four projects while the board beside it drew forty.
          `null` means the rollup has no honest answer for the question being
          asked (the endpoint takes no search term), and the strip falls silent
          rather than answering a wider one. It fetches on its own and fails on
          its own; nothing here waits for it, so a rollup that never arrives
          costs the register nothing. */}
      <PortfolioStrip params={portfolioParams(view)} />

      {/* This operator's own shortlist, above the filters rather than inside the
          list - because it must answer the same way under every filter, every
          search term and every page. Sorting the pins to the top of a paged,
          server-sorted register would leave a pinned project on page three with
          nothing on screen admitting it; see the strip's own file. It draws
          nothing at all when nothing is pinned. */}
      <PinnedProjects epoch={pinEpoch} onChanged={() => void load()} />

      <ProjectsToolbar q={qInput} onQ={setQInput} filters={view} onPatch={onFilter} onClear={onClear} />

      {loading ? (
        <div className="crm-card flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : view.view === "roadmap" ? (
        <>
          <RoadmapBoard rows={rows} onChanged={patchRow} onReload={() => void load()} />
          {/* The board is not paged, so it stops at BOARD_SIZE and says nothing
              about it. The portfolio strip above answers for the WHOLE filtered
              set, so past the ceiling the header would be counting projects the
              columns do not draw - the same lie this file refuses for the
              25-row list. Drawn only when it is true, because a footer that is
              always there stops being read. */}
          {total > rows.length ? (
            <p className="crm-mut px-1 pt-3 text-[12.5px]">
              {t("projects.showing", { from: 1, to: rows.length, total })}
            </p>
          ) : null}
        </>
      ) : (
        <div className="crm-card" style={listCard(PROJECT_COLS)}>
          {rows.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title={t("projects.empty")}
              body={t("projects.emptyBody")}
              action={
                <Btn tone="brand" onClick={() => setShowNew(true)}>
                  <Plus className="size-4" />
                  {t("projects.add")}
                </Btn>
              }
            />
          ) : (
            <>
              {/* Three of the six columns can be ordered by. The three that
                  cannot are decisions, not omissions:

                  STATUS has no sort on the server, and alphabetical order over
                  five lifecycle words is not an order anybody wants - the filter
                  beside it answers the question a status sort would be asked.

                  PROGRESS is the trap. This column prints
                  `effectiveProgressPct`, resolved per row after the page comes
                  back, while the database can only order by the STORED
                  `progressPct` - a different number on every project whose
                  progress follows its tasks. A head here would claim to sort the
                  figures on screen and would quietly sort something else, which
                  is worse than not offering the sort at all.

                  TASKS is a count folded out of a groupBy over the fetched page,
                  so ordering by it would order one page against itself and bear
                  no relation to page two. */}
              <div className="crm-list-head" style={{ gridTemplateColumns: PROJECT_COLS, paddingTop: 22 }}>
                <SortHead label={t("projects.col.name")} column="name" sort={view.sort} order={view.dir} onSort={onSort} />
                <span>{t("projects.statusCol")}</span>
                <SortHead label={t("projects.deadline")} column="deadline" sort={view.sort} order={view.dir} onSort={onSort} />
                <span>{t("projects.progress")}</span>
                <SortHead label={t("projects.budget")} column="budget" sort={view.sort} order={view.dir} onSort={onSort} />
                <span>{t("projects.tasks")}</span>
                <span />
              </div>

              <div className="crm-rows pt-0">
                {rows.map((p) => (
                  <ProjectsListRow
                    key={p.id}
                    project={p}
                    onChanged={patchRow}
                    onDelete={() => setConfirmDel(p)}
                    // A refetch rather than a row patch: whether an archived
                    // project should still be on screen depends on the archive
                    // filter this list is under, and the count, the pager and
                    // the portfolio strip all have to move with it.
                    onArchiveChanged={() => void load()}
                    // The row is patched in place and the strip is told to ask
                    // again. No refetch of the list: a pin changes nothing about
                    // WHICH projects belong in it, unlike the archive above -
                    // and reloading twenty-five rows to move a badge would put
                    // the register through a spinner for a bookmark.
                    onPinChanged={(pinned) => {
                      patchRow({ ...p, pinned });
                      setPinEpoch((n) => n + 1);
                    }}
                  />
                ))}
              </div>

              <div className="crm-tfoot">
                {t("projects.showing", {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: (page - 1) * PAGE_SIZE + rows.length,
                  total,
                })}
                <span className="crm-push flex gap-2.5">
                  <Btn small disabled={page <= 1} onClick={() => goPage(page - 1)}>
                    <ChevronLeft className="size-4" />
                    {t("common.prev")}
                  </Btn>
                  <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => goPage(page + 1)}>
                    {t("common.next")}
                    <ChevronRight className="size-4" />
                  </Btn>
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {showNew ? <NewProjectModal onClose={() => setShowNew(false)} onCreate={() => void load()} /> : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("projects.deleteTitle")}
          body={t("projects.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => removeProject(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
